import React, { useState, useEffect, useCallback, useRef } from 'react';
import accountingService from '../../../../services/accountingService';
import type { Customer } from '../types/customer.types'; // ✅ Merkezi Customer tipi

// Re-export for backward compatibility
export type { Customer };

// Constants
const GENERAL_CUSTOMER_ID = 'GENERAL_CUSTOMER_ACCOUNT';
const GENERAL_CUSTOMER_NAME = 'Genel Cari';
const CUSTOMERS_KEY = 'quickSaleCustomers';
const CUSTOMERS_TIMESTAMP_KEY = 'quickSaleCustomersTimestamp';
const CACHE_VALIDITY_MS = 24 * 60 * 60 * 1000; // 24 saat

interface UseQuickSaleCustomersProps {
  companyId: string | null;
  selectedCustomer: Customer | null;
  setSelectedCustomer: React.Dispatch<React.SetStateAction<Customer | null>>;
  showAlertModal: (title: string, message: string) => void;
  isPackageMode?: boolean; // ✅ YENİ: Paket modunda otomatik cari seçimi yapma
}

interface UseQuickSaleCustomersReturn {
  customers: Customer[];
  selectedCustomer: Customer | null;
  setSelectedCustomer: React.Dispatch<React.SetStateAction<Customer | null>>;
  generalCariRef: Customer | null;
  loadingCustomers: boolean;
  customerError: string | null;
  calculateBalance: (customer: Customer) => number;
  refreshCustomers: () => Promise<Customer[]>;
  isCustomerSelectionModalOpen: boolean;
  setIsCustomerSelectionModalOpen: (open: boolean) => void;
  handleOpenCustomerSelectionModal: () => void;
  handleCloseCustomerSelectionModal: () => void;
  handleSelectCustomerFromModal: (customer: Customer) => void;
  allowAccountCreationOnSale: boolean;
  setAllowAccountCreationOnSale: (allow: boolean) => void;
}

export const useQuickSaleCustomers = ({
  companyId,
  selectedCustomer,
  setSelectedCustomer,
  showAlertModal,
  isPackageMode = false // ✅ YENİ: Paket modunda otomatik cari seçimi yapma
}: UseQuickSaleCustomersProps): UseQuickSaleCustomersReturn => {
  // State declarations
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [generalCariRef, setGeneralCariRef] = useState<Customer | null>(null);
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [customerError, setCustomerError] = useState<string | null>(null);
  const [isCustomerSelectionModalOpen, setIsCustomerSelectionModalOpen] = useState(false);
  const [allowAccountCreationOnSale, setAllowAccountCreationOnSale] = useState<boolean>(false);
  const retryCountRef = useRef(0);
  const MAX_RETRIES = 3;
  const RETRY_DELAY_MS = 3000;

  // Calculate customer balance
  const calculateBalance = useCallback((customer: Customer): number => {
    const debitBalance = customer.debitBalance || 0; 
    const creditBalance = customer.creditBalance !== undefined ? customer.creditBalance : (customer.balance || 0);
    return creditBalance - debitBalance;
  }, []);

  // Load customers from database with localStorage cache
  useEffect(() => {
    const loadCustomers = async () => {
      if (!companyId) {
        console.log("Şirket ID henüz hazır değil, cari hesaplar yüklenemedi.");
        return;
      }

      setLoadingCustomers(true);
      setCustomerError(null);

      try {
        // Try loading from localStorage first
        let cachedCustomers: Customer[] | null = null;
        let cachedTimestamp: number | null = null;
        
        try {
          const cachedCustomersJson = localStorage.getItem(`${CUSTOMERS_KEY}_${companyId}`);
          const cachedTimestampStr = localStorage.getItem(`${CUSTOMERS_TIMESTAMP_KEY}_${companyId}`);
          
          if (cachedCustomersJson && cachedTimestampStr) {
            cachedCustomers = JSON.parse(cachedCustomersJson);
            cachedTimestamp = parseInt(cachedTimestampStr, 10);
            console.log(`[useQuickSaleCustomers] ${cachedCustomers?.length || 0} cari önbellekten yüklendi. Önbellek yaşı: ${Math.round((Date.now() - cachedTimestamp!) / (60 * 1000))} dakika`);
          }
        } catch (error) {
          console.error("LocalStorage'dan cari verileri okunurken hata:", error);
          cachedCustomers = null;
          cachedTimestamp = null;
        }
        
        // ❌ ÖNBELLEK ARTIK KULLANILMIYOR - Her zaman SQL'den "Genel Cari" çekiyoruz
        // Önbellek sistemini kaldırdık çünkü sadece 1 cari yüklüyoruz (performans sorunu yok)

        // ✅ YENİ: SQL'den SADECE "Genel Cari" çek (performans optimizasyonu)
        console.log(`[useQuickSaleCustomers] 🔄 SQL'den SADECE Genel Cari çekiliyor...`);
        const generalAccount = await accountingService.getGeneralAccount();
        
        let customerList: Customer[] = [];
        let foundGeneralCustomer: Customer | null = null;
          
        if (generalAccount) {
          // ✅ FIX: Backend camelCase dönüyor, mapping yaparken bunu dikkate al
          const rawAccount = generalAccount as any;
          foundGeneralCustomer = {
            id: (generalAccount.Id || rawAccount.id)?.toString() || '',
            companyName: generalAccount.CompanyName || rawAccount.companyName || GENERAL_CUSTOMER_NAME,
            balance: generalAccount.CreditBalance || rawAccount.creditBalance || undefined,
            debitBalance: generalAccount.DebitBalance || rawAccount.debitBalance || 0,
            creditBalance: generalAccount.CreditBalance || rawAccount.creditBalance || 0,
            mobilePhone: generalAccount.MobilePhone || rawAccount.mobilePhone || '',
            email: generalAccount.Email || rawAccount.email || '',
            isActive: (generalAccount.IsActive !== false) && (rawAccount.isActive !== false),
            taxNumber: generalAccount.TaxNumber || rawAccount.taxNumber || undefined,
            taxOffice: generalAccount.TaxOffice || rawAccount.taxOffice || undefined,
            address: generalAccount.Address || rawAccount.address || undefined,
          };
          
          customerList.push(foundGeneralCustomer);
          console.log(`[useQuickSaleCustomers] ✅ Genel Cari SQL'den yüklendi`);
        } else {
          console.warn("[useQuickSaleCustomers] ⚠️ SQL'de Genel Cari bulunamadı!");
        }

        setCustomers(customerList);

        // Set default selection only if no customer is selected yet
        // ✅ PAKET MODUNDA OTOMATİK SEÇİM YAPMA - Paket müşterisi ayrıca seçilecek
        if (!selectedCustomer && !isPackageMode) {
          if (foundGeneralCustomer) {
            setSelectedCustomer(foundGeneralCustomer);
            setGeneralCariRef(foundGeneralCustomer);
          } else if (customerList.length > 0) {
            setSelectedCustomer(null); 
            showAlertModal("Uyarı", "Varsayılan 'Genel Cari' hesabı bulunamadı. Lütfen manuel olarak bir cari seçin veya sistem yöneticinize başvurun.");
          } else {
            setSelectedCustomer(null);
          }
        }

        // Set generalCariRef if found and not already set
        if (foundGeneralCustomer && !generalCariRef) {
          setGeneralCariRef(foundGeneralCustomer);
        }
        
        // ❌ ARTIK ÖNBELLEĞE KAYDETMIYORUZ - Sadece 1 cari (Genel Cari) var
        console.log(`[useQuickSaleCustomers] ${customerList.length} cari SQL'den yüklendi (Önbellek yok).`);

      } catch (error) {
        console.error("Cari hesaplar yüklenirken hata:", error);
        setCustomerError("Cari hesaplar yüklenirken bir hata oluştu.");
        setCustomers([]); 

        // Retry mekanizması: API hatası durumunda otomatik tekrar dene
        if (retryCountRef.current < MAX_RETRIES) {
          retryCountRef.current += 1;
          const delay = RETRY_DELAY_MS * retryCountRef.current;
          console.warn(`[useQuickSaleCustomers] ⚠️ Cari yükleme başarısız, ${delay}ms sonra tekrar denenecek (deneme ${retryCountRef.current}/${MAX_RETRIES})`);
          setTimeout(() => {
            loadCustomers();
          }, delay);
        } else {
          console.error(`[useQuickSaleCustomers] ❌ ${MAX_RETRIES} deneme sonrası cari yüklenemedi!`);
          setSelectedCustomer(null);
        }
      } finally {
        setLoadingCustomers(false);
      }
    };

    retryCountRef.current = 0;
    loadCustomers();
  }, [companyId]); // Only depend on companyId

  // selectedCustomer null'a düşerse ve generalCariRef varsa otomatik kurtarma
  useEffect(() => {
    if (!selectedCustomer && generalCariRef && !loadingCustomers && !isPackageMode) {
      console.warn('[useQuickSaleCustomers] ⚠️ selectedCustomer null, generalCariRef ile kurtarılıyor:', generalCariRef.companyName);
      setSelectedCustomer(generalCariRef);
    }
  }, [selectedCustomer, generalCariRef, loadingCustomers, isPackageMode, setSelectedCustomer]);

  // selectedCustomer listede yoksa otomatik ekle (MUI Autocomplete uyarısını önler)
  useEffect(() => {
    if (selectedCustomer && selectedCustomer.id !== GENERAL_CUSTOMER_ID) {
      setCustomers(prev => {
        const exists = prev.some(c => c.id === selectedCustomer.id);
        if (!exists) {
          return [...prev, selectedCustomer];
        }
        return prev;
      });
    }
  }, [selectedCustomer]);

  // Refresh customers function
  const refreshCustomers = useCallback(async (): Promise<Customer[]> => {
    if (!companyId) {
      setCustomerError("Cari hesaplar güncellenemedi: Şirket ID eksik.");
      return [];
    }

    setLoadingCustomers(true);
    setCustomerError(null);

    try {
      // ✅ YENİ: SQL'den SADECE "Genel Cari" çek
      console.log(`[useQuickSaleCustomers] 🔄 Genel Cari yenileniyor (SQL)...`);
      const generalAccount = await accountingService.getGeneralAccount();
      
      let customerList: Customer[] = [];
      let foundGeneralCustomer: Customer | null = null;
        
      if (generalAccount) {
        // ✅ FIX: Backend camelCase dönüyor, mapping yaparken bunu dikkate al
        const rawAccount = generalAccount as any;
        foundGeneralCustomer = {
          id: (generalAccount.Id || rawAccount.id)?.toString() || '',
          companyName: generalAccount.CompanyName || rawAccount.companyName || GENERAL_CUSTOMER_NAME,
          balance: generalAccount.CreditBalance || rawAccount.creditBalance || undefined,
          debitBalance: generalAccount.DebitBalance || rawAccount.debitBalance || 0,
          creditBalance: generalAccount.CreditBalance || rawAccount.creditBalance || 0,
          mobilePhone: generalAccount.MobilePhone || rawAccount.mobilePhone || '',
          email: generalAccount.Email || rawAccount.email || '',
          isActive: (generalAccount.IsActive !== false) && (rawAccount.isActive !== false),
          taxNumber: generalAccount.TaxNumber || rawAccount.taxNumber || undefined,
          taxOffice: generalAccount.TaxOffice || rawAccount.taxOffice || undefined,
          address: generalAccount.Address || rawAccount.address || undefined,
        };
        
        customerList.push(foundGeneralCustomer);
        console.log(`[useQuickSaleCustomers] ✅ Genel Cari yenilendi`);
      } else {
        console.warn("[useQuickSaleCustomers] ⚠️ SQL'de Genel Cari bulunamadı!");
      }

      setCustomers(customerList);
      
      // Set default selection if needed
      // ✅ PAKET MODUNDA OTOMATİK SEÇİM YAPMA
      if (!selectedCustomer && foundGeneralCustomer && !isPackageMode) {
        setSelectedCustomer(foundGeneralCustomer);
      }
      
      // ❌ ARTIK ÖNBELLEĞE KAYDETMIYORUZ - Sadece 1 cari var

      return customerList;
    } catch (error) {
      console.error("Cari hesaplar yüklenirken hata:", error);
      setCustomerError("Cari hesaplar yüklenirken bir hata oluştu.");
      setCustomers([]);
      return [];
    } finally {
      setLoadingCustomers(false);
    }
  }, [companyId, selectedCustomer, setSelectedCustomer, isPackageMode]);

  // ❌ Real-time listener kaldırıldı - SQL'de real-time yok
  // Bakiye güncellemesi için manuel refresh (refreshCustomers) kullanılacak

  // Customer selection modal handlers
  const handleOpenCustomerSelectionModal = useCallback(() => {
    setIsCustomerSelectionModalOpen(false);
    setTimeout(() => {
      setIsCustomerSelectionModalOpen(true);
    }, 50);
  }, []);

  const handleCloseCustomerSelectionModal = useCallback(() => {
    setIsCustomerSelectionModalOpen(false);
  }, []);

  const handleSelectCustomerFromModal = useCallback((customer: Customer) => {
    setSelectedCustomer(customer);
    setIsCustomerSelectionModalOpen(false);
  }, [setSelectedCustomer]);

  return {
    customers,
    selectedCustomer,
    setSelectedCustomer,
    generalCariRef,
    loadingCustomers,
    customerError,
    calculateBalance,
    refreshCustomers,
    isCustomerSelectionModalOpen,
    setIsCustomerSelectionModalOpen,
    handleOpenCustomerSelectionModal,
    handleCloseCustomerSelectionModal,
    handleSelectCustomerFromModal,
    allowAccountCreationOnSale,
    setAllowAccountCreationOnSale
  };
};

